import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, User, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const NavLinks = ({ onClick }) => (
    <>
      <Link data-testid="nav-discover" to="/discover" onClick={onClick} className="text-ink hover:text-moss transition-colors">Discover</Link>
      <Link data-testid="nav-pillars" to="/#pillars" onClick={onClick} className="text-ink hover:text-moss transition-colors">Four Pillars</Link>
      <Link data-testid="nav-host" to="/for-hosts" onClick={onClick} className="text-ink hover:text-moss transition-colors">For Hosts</Link>
      <Link data-testid="nav-about" to="/#about" onClick={onClick} className="text-ink hover:text-moss transition-colors">Journal</Link>
    </>
  );

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-canvas/80 border-b border-subtle">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link data-testid="brand-logo" to="/" className="group flex flex-col leading-none">
          <span className="font-serif font-bold text-3xl md:text-4xl tracking-tight text-ink">
            Retreat Travel<span className="text-moss">.</span>
          </span>
          <span className="hidden sm:block font-serif font-bold italic text-sm md:text-base text-[#E63946] mt-1 tracking-wide">
            Discover Travel That Transforms Lives.
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <NavLinks />
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full bg-white border border-subtle px-3 py-1.5 hover:border-moss transition-colors">
                  <span className="w-7 h-7 rounded-full bg-moss text-canvas flex items-center justify-center text-xs font-semibold">
                    {user.name?.charAt(0)?.toUpperCase()}
                  </span>
                  <span className="text-sm">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate(user.role === "admin" ? "/admin" : (user.role === "host" ? "/host" : "/traveler"))}>
                  <User className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate("/messages")}>
                  Messages
                </DropdownMenuItem>
                {user.role === "traveler" && (
                  <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/favorites")}>
                    Favorites
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="text-sm text-ink hover:text-moss">Sign in</Link>
              <Button data-testid="nav-join" onClick={() => navigate("/register")} className="rounded-full bg-moss hover:bg-moss-hover text-canvas px-5">
                Begin
              </Button>
            </>
          )}
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-trigger" className="md:hidden p-2">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 bg-canvas">
            <div className="flex flex-col gap-6 mt-8">
              <NavLinks />
              {user ? (
                <>
                  <Link data-testid="mobile-dashboard" to={user.role === "host" ? "/host" : "/traveler"}>Dashboard</Link>
                  <button data-testid="mobile-logout" onClick={async () => { await logout(); navigate("/"); }} className="text-left">Sign out</button>
                </>
              ) : (
                <>
                  <Link data-testid="mobile-login" to="/login">Sign in</Link>
                  <Link data-testid="mobile-register" to="/register">Begin</Link>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
